import { h, toast, confirmDialog, tableScroll } from '../components/dom.js';
import { renderShell, pageHead, portalFooter } from '../components/shell.js';
import { api } from '../api/client.js';
import { esc } from './dashboard.js';
import { groupByCategory, enableState, slug, validateFieldValue, fieldInputType, formatPlaceholder, toDateInputValue, fromDateInputValue, canonicalizeFieldValue, typeDisplayName, qualifierSubline, isCatalogType } from './fields-catalog.js';
import type { FieldType, FieldInstance, Profile } from '../api/types.js';

export async function renderFields(): Promise<void> {
  const main = renderShell('/fields');
  const addBtn = h('button', { class: 'btn btn-primary btn-sm' }, 'Add fields');
  main.append(pageHead('My Info', 'Your master identity. Enable ready-to-go standardized fields or add your own.', addBtn));

  const [fieldsRes, profilesRes, typesRes] = await Promise.all([
    api.fields(), api.profiles(), api.fieldTypes().catch(() => ({ data: [] as FieldType[] })),
  ]);
  const profiles = profilesRes.data;

  // Map well-known field-type key -> validation format (from the catalog API).
  const fmtByKey = new Map<string, string>();
  for (const t of typesRes.data) if (t.format) fmtByKey.set(t.key, t.format);
  const fieldFormat = (f: FieldInstance): string | undefined =>
    fmtByKey.get(f.field_type_key || f.type || '');

  // Build exposure lookup: fieldId -> profiles exposing it.
  const exposureByField = new Map<string, Profile[]>();
  await Promise.all(profiles.map(async (p) => {
    try {
      const exp = await api.exposures(p.id);
      for (const e of exp.data) if (e.exposed) {
        const arr = exposureByField.get(e.field_id) || [];
        arr.push(p); exposureByField.set(e.field_id, arr);
      }
    } catch { /* ignore */ }
  }));

  const tbody = h('tbody');
  const catalogTypes = typesRes.data;
  const renderRow = (f: FieldInstance): HTMLElement => {
    const exposed = exposureByField.get(f.id) || [];
    const badges = exposed.length
      ? exposed.map((p) => h('span', { class: 'badge ' + (p.is_public ? 'badge-ok' : 'badge-brand') }, p.name))
      : [h('span', { class: 'muted' }, 'Not exposed')];
    const valueCell = h('td', {}, f.value || '—');
    valueCell.addEventListener('dblclick', () => editValue(f.id, valueCell, f.value || '', fieldFormat(f)));

    const editBtn = h('button', { class: 'btn btn-ghost btn-sm', title: 'Edit value' }, 'Edit');
    editBtn.addEventListener('click', () => editValue(f.id, valueCell, f.value || '', fieldFormat(f)));
    const removeBtn = h('button', { class: 'btn btn-ghost btn-sm', title: 'Remove field', style: 'color:var(--danger)' }, 'Remove');
    removeBtn.addEventListener('click', async () => {
      const name = typeDisplayName(f.field_type_key || f.type, catalogTypes);
      if (!confirmDialog(`Remove “${name}”? It will be deleted from your identity and unexposed from every profile.`)) return;
      (removeBtn as HTMLButtonElement).disabled = true; removeBtn.textContent = 'Removing…';
      try {
        await api.deleteField(f.id);
        row.remove();
        toast('Field removed — subscribers refreshed', 'ok');
      } catch (e) {
        toast((e as Error).message, 'danger');
        (removeBtn as HTMLButtonElement).disabled = false; removeBtn.textContent = 'Remove';
      }
    });

    // First cell: for CATALOG fields the PRIMARY is the type display name and
    // `label` is an optional qualifier sub-line (e.g. "(work)"), editable via
    // pencil / dblclick. For CUSTOM fields, `label` is the field's display NAME
    // (no catalog), so it is the primary and there is no qualifier line.
    const isCatalog = isCatalogType(f.field_type_key || f.type, catalogTypes);
    // Native hover-hint (browser `title`) showing ONLY the raw field id/key
    // (lowercase, underscores) on the friendly type-name element.
    const idTitle = f.field_type_key || f.type || '';
    const nameCell = h('td', {});
    if (isCatalog) {
      const typeName = typeDisplayName(f.field_type_key || f.type, catalogTypes);
      const sub = qualifierSubline(f.label);
      const qualLine = h('div', { class: 'field-qualifier muted', title: 'Double-click to edit annotation',
        style: 'font-size:.82em;color:var(--muted);cursor:pointer' },
        sub || h('span', { style: 'opacity:.5' }, '＋ annotation'));
      qualLine.addEventListener('dblclick', () => editLabel(f, qualLine));
      const editQualBtn = h('button', { class: 'btn btn-ghost btn-sm', title: 'Edit annotation', style: 'padding:0 4px;font-size:.8em' }, '✎');
      editQualBtn.addEventListener('click', () => editLabel(f, qualLine));
      nameCell.append(
        h('div', { title: idTitle, html: esc(typeName) + (f.sensitivity === 'high' ? ' <span class="badge badge-warn">high sensitivity</span>' : '') }),
        h('div', { class: 'row', style: 'gap:4px;align-items:center' }, qualLine, editQualBtn));
    } else {
      const displayName = f.custom?.name || f.label || (f.field_type_key || f.type || '');
      // Custom fields store their display NAME in `label`, so their optional
      // qualifier lives in the dedicated `annotation` slot — editable the same
      // way catalog fields edit their `label` qualifier.
      const sub = qualifierSubline(f.annotation);
      const qualLine = h('div', { class: 'field-qualifier muted', title: 'Double-click to edit annotation',
        style: 'font-size:.82em;color:var(--muted);cursor:pointer' },
        sub || h('span', { style: 'opacity:.5' }, '＋ annotation'));
      qualLine.addEventListener('dblclick', () => editAnnotation(f, qualLine));
      const editQualBtn = h('button', { class: 'btn btn-ghost btn-sm', title: 'Edit annotation', style: 'padding:0 4px;font-size:.8em' }, '✎');
      editQualBtn.addEventListener('click', () => editAnnotation(f, qualLine));
      nameCell.append(
        h('div', { title: idTitle, html: esc(displayName) + (f.sensitivity === 'high' ? ' <span class="badge badge-warn">high sensitivity</span>' : '') }),
        h('div', { class: 'row', style: 'gap:4px;align-items:center' }, qualLine, editQualBtn));
    }

    const row = h('tr', {},
      nameCell,
      h('td', {}, f.custom?.base_type || (f.field_type_key || f.type || '').split('_')[0]),
      valueCell,
      h('td', {}, badges),
      h('td', { style: 'white-space:nowrap;text-align:right' },
        h('span', { class: 'row', style: 'gap:6px;justify-content:flex-end' }, editBtn, removeBtn)));
    return row;
  };
  for (const f of fieldsRes.data) tbody.append(renderRow(f));
  main.append(h('p', { class: 'muted', style: 'margin:0 0 8px' },
    h('small', {}, 'Tip: double-tap a field value to edit it (or use the Edit button).')));
  main.append(h('div', { class: 'card', style: 'padding:0' },
    tableScroll(h('table', { class: 'table' },
      h('thead', {}, h('tr', {}, h('th', {}, 'Field'), h('th', {}, 'Type'), h('th', {}, 'Value'), h('th', {}, 'Exposed to'), h('th', { style: 'text-align:right' }, 'Actions'))),
      tbody))));
  main.append(h('p', { class: 'muted', style: 'margin-top:14px' },
    h('small', {}, 'Use Edit to change a value (or double-click it). Sensitive values are masked and encrypted at rest. Editing a value refreshes every subscriber automatically.')));
  main.append(portalFooter());

  addBtn.addEventListener('click', () => openAddFields(typesRes.data, fieldsRes.data));
}

async function editValue(fieldId: string, cell: HTMLElement, current: string, fmt?: string): Promise<void> {
  const inputType = fieldInputType(fmt);
  // For a native date input, the value must be YYYY-MM-DD; store is YYYY/MM/DD.
  const initial = inputType === 'date' ? toDateInputValue(current) : current;
  const attrs: Record<string, string> = { class: 'input', value: initial };
  if (inputType === 'date') attrs.type = 'date';
  else { const ph = formatPlaceholder(fmt); if (ph) attrs.placeholder = ph; }
  const input = h('input', attrs) as HTMLInputElement;
  cell.replaceChildren(input); input.focus();
  let saving = false;
  const save = async () => {
    if (saving) return;
    // Convert native date value back to canonical YYYY/MM/DD before anything.
    const raw = inputType === 'date' ? fromDateInputValue(input.value) : input.value;
    if (raw === current) { cell.textContent = current || '—'; return; }
    const msg = validateFieldValue(fmt, raw);
    if (msg) { toast(msg, 'warn'); input.focus(); return; }
    saving = true;
    try { const f = await api.updateField(fieldId, { value: raw }); cell.textContent = f.value || '—'; toast('Field updated — subscribers refreshed', 'ok'); }
    catch (e) { toast((e as Error).message, 'danger'); cell.textContent = current || '—'; }
  };
  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') input.blur(); });
}

// editLabel inline-edits a field's QUALIFIER/annotation (the reused `label`
// column, e.g. "work"). PATCHes only `label` so the value is untouched, then
// re-renders the muted sub-line. Empty clears the annotation.
async function editLabel(f: FieldInstance, cell: HTMLElement): Promise<void> {
  const current = f.label || '';
  const input = h('input', { class: 'input', value: current, placeholder: 'e.g. work, private',
    style: 'font-size:.85em;padding:2px 6px' }) as HTMLInputElement;
  cell.replaceChildren(input); input.focus();
  let saving = false;
  const restore = () => {
    const sub = qualifierSubline(f.label);
    cell.replaceChildren(sub || h('span', { style: 'opacity:.5' }, '＋ annotation'));
  };
  const save = async () => {
    if (saving) return;
    const next = input.value.trim();
    if (next === current) { restore(); return; }
    saving = true;
    try {
      const updated = await api.updateField(f.id, { label: next });
      f.label = updated.label ?? next;
      restore();
      toast('Annotation updated — subscribers refreshed', 'ok');
    } catch (e) { toast((e as Error).message, 'danger'); restore(); }
  };
  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') input.blur(); });
}

// editAnnotation inline-edits a CUSTOM field's QUALIFIER (the dedicated
// `annotation` column, e.g. "work"). Mirrors editLabel but PATCHes `annotation`
// so the custom field's display NAME (kept in `label`) is untouched.
async function editAnnotation(f: FieldInstance, cell: HTMLElement): Promise<void> {
  const current = f.annotation || '';
  const input = h('input', { class: 'input', value: current, placeholder: 'e.g. work, private',
    style: 'font-size:.85em;padding:2px 6px' }) as HTMLInputElement;
  cell.replaceChildren(input); input.focus();
  let saving = false;
  const restore = () => {
    const sub = qualifierSubline(f.annotation);
    cell.replaceChildren(sub || h('span', { style: 'opacity:.5' }, '＋ annotation'));
  };
  const save = async () => {
    if (saving) return;
    const next = input.value.trim();
    if (next === current) { restore(); return; }
    saving = true;
    try {
      const updated = await api.updateField(f.id, { annotation: next });
      f.annotation = updated.annotation ?? next;
      restore();
      toast('Annotation updated — subscribers refreshed', 'ok');
    } catch (e) { toast((e as Error).message, 'danger'); restore(); }
  };
  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') input.blur(); });
}

// openAddFields shows the "Add fields" modal: the well-known catalog grouped by
// category (each row enable-able), plus a separate "Add custom field" section.
function openAddFields(types: FieldType[], currentFields: FieldInstance[]): void {
  // Local mutable snapshot of current fields so enable-state updates as we add.
  const current: FieldInstance[] = currentFields.slice();
  let dirty = false;

  const overlay = h('div', { style: 'position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:flex-start;justify-content:center;z-index:50;padding:32px 20px;overflow:auto', onclick: () => close() });
  const close = () => { overlay.remove(); if (dirty) renderFields(); };

  const catalogBody = h('div', { class: 'catalog-list' });

  function renderCatalog(): void {
    catalogBody.replaceChildren();
    if (!types.length) {
      catalogBody.append(h('p', { class: 'muted' }, 'No standardized field catalog available. Use “Add custom field” below.'));
      return;
    }
    for (const group of groupByCategory(types)) {
      catalogBody.append(h('h4', { class: 'catalog-group' }, group.label));
      for (const t of group.types) catalogBody.append(catalogRow(t));
    }
  }

  function catalogRow(t: FieldType): HTMLElement {
    const st = enableState(t, current);
    const meta = [t.category || 'misc', t.multi_value ? 'multi' : '', t.sensitivity === 'high' ? 'sensitive' : '']
      .filter(Boolean).join(' · ');
    const btn = h('button', { class: 'btn btn-sm ' + (st.disabled ? 'btn-ghost' : 'btn-secondary') },
      st.disabled ? 'Added' : (st.added ? 'Add another' : 'Enable')) as HTMLButtonElement;
    if (st.disabled) btn.disabled = true;
    btn.addEventListener('click', async () => {
      btn.disabled = true; btn.textContent = 'Adding…';
      try {
        const f = await api.addField({ field_type_key: t.key, value: '' });
        current.push(f); dirty = true;
        toast(`${t.label || t.key} added`, 'ok');
        renderCatalog();
      } catch (e) { toast((e as Error).message, 'danger'); renderCatalog(); }
    });
    return h('div', { class: 'catalog-row' + (st.disabled ? ' is-added' : '') },
      h('div', {},
        h('div', { class: 'catalog-row-label' }, t.label || t.key,
          st.count > 1 ? h('span', { class: 'badge badge-brand', style: 'margin-left:6px' }, `×${st.count}`) : null),
        h('small', { class: 'muted' }, meta)),
      btn);
  }

  renderCatalog();

  const card = h('div', { class: 'card', style: 'max-width:560px;width:100%', onclick: (e: Event) => e.stopPropagation() },
    h('h3', {}, 'Add fields'),
    h('p', { class: 'muted', style: 'margin-top:-4px' }, h('small', {}, 'Enable a ready-to-go standardized field, then fill its value in the table. Need something non-standard? Add a custom field.')),
    catalogBody,
    h('hr', { class: 'divider' }),
    customSection(current, () => { dirty = true; }),
    h('button', { class: 'btn btn-ghost btn-block', style: 'margin-top:14px', onclick: () => close() }, 'Done'));
  overlay.append(card); document.body.append(overlay);
}

// customSection builds the "＋ Add custom field" affordance for non-standard fields.
function customSection(current: FieldInstance[], onAdded: () => void): HTMLElement {
  const label = h('input', { class: 'input', placeholder: 'e.g. Gamer Tag, Office extension, LinkedIn handle' }) as HTMLInputElement;
  const idInput = h('input', { class: 'input', placeholder: 'custom_my_something' }) as HTMLInputElement;
  // Custom-field type options. Each maps to a storage base_type plus an optional
  // validation `format` that drives the value placeholder + validation. This is
  // where the newer formats (e.g. short_date) are surfaced for custom fields.
  const TYPE_OPTIONS: { value: string; label: string; base_type: 'text' | 'number' | 'date' | 'url' | 'bool'; format?: string }[] = [
    { value: 'text', label: 'text', base_type: 'text' },
    { value: 'number', label: 'number', base_type: 'number' },
    { value: 'date', label: 'date (YYYY/MM/DD)', base_type: 'date', format: 'date' },
    { value: 'short_date', label: 'short date (MM/DD)', base_type: 'date', format: 'short_date' },
    { value: 'url', label: 'url', base_type: 'url' },
    { value: 'bool', label: 'bool', base_type: 'bool' },
  ];
  const baseType = h('select', { class: 'select' },
    ...TYPE_OPTIONS.map((o) => h('option', { value: o.value }, o.label))) as HTMLSelectElement;
  const selectedType = () => TYPE_OPTIONS.find((o) => o.value === baseType.value) || TYPE_OPTIONS[0];
  const value = h('input', { class: 'input', placeholder: 'Value (optional)' }) as HTMLInputElement;
  // Populate the value placeholder with the selected type's pattern hint (gray
  // background text), e.g. YYYY/MM/DD for date, so the user knows what to enter.
  const syncValuePlaceholder = () => {
    const ph = formatPlaceholder(selectedType().format);
    value.placeholder = ph || 'Value (optional)';
  };
  baseType.addEventListener('change', syncValuePlaceholder);
  syncValuePlaceholder();
  const add = h('button', { class: 'btn btn-primary btn-sm' }, '＋ Add custom field') as HTMLButtonElement;

  // Auto-sync the field id from the friendly name until the user edits the id
  // manually. `programmatic` guards our own writes so they don't trip the flag.
  let idManuallyEdited = false;
  let programmatic = false;
  const setId = (v: string) => { programmatic = true; idInput.value = v; programmatic = false; };
  label.addEventListener('input', () => {
    if (idManuallyEdited) return;
    const s = slug(label.value);
    setId(s ? 'custom_' + s : '');
  });
  idInput.addEventListener('input', () => {
    if (programmatic) return;
    // Re-enable auto-sync only when the user clears the id entirely.
    idManuallyEdited = idInput.value.trim() !== '';
  });

  // ensureCustomId returns the stored field id, guaranteeing a custom_ prefix.
  const ensureCustomId = (): string => {
    let id = idInput.value.trim();
    if (!id) id = 'custom_' + slug(label.value);
    if (!id.startsWith('custom_')) id = 'custom_' + id.replace(/^_+/, '');
    return id;
  };

  add.addEventListener('click', async () => {
    if (!label.value.trim()) { toast('Custom field needs a name', 'warn'); return; }
    add.disabled = true;
    try {
      const customId = ensureCustomId();
      const opt = selectedType();
      // Validate + canonicalize the value against the chosen type's format
      // (e.g. a date must be YYYY/MM/DD) before sending it to the server.
      const rawValue = value.value.trim();
      const msg = validateFieldValue(opt.format, rawValue);
      if (msg) { toast(msg, 'warn'); value.focus(); add.disabled = false; return; }
      const outValue = canonicalizeFieldValue(opt.format, rawValue);
      const f = await api.addField({
        field_type_key: customId, label: label.value.trim(), value: outValue,
        custom: { name: label.value.trim(), base_type: opt.base_type },
      });
      current.push(f); onAdded();
      toast('Custom field added', 'ok');
      label.value = ''; value.value = ''; setId(''); idManuallyEdited = false;
      baseType.selectedIndex = 0; syncValuePlaceholder();
    } catch (e) { toast((e as Error).message, 'danger'); }
    finally { add.disabled = false; }
  });
  return h('div', { class: 'custom-field-section' },
    h('h4', { class: 'catalog-group' }, 'Add custom field'),
    h('label', { class: 'field-label' }, h('small', {}, 'Friendly name'), label),
    h('label', { class: 'field-label', style: 'margin-top:8px' }, h('small', {}, 'Field ID'), idInput),
    h('div', { class: 'custom-field-row', style: 'margin-top:8px' }, baseType, value),
    h('div', { style: 'margin-top:8px' }, add));
}
